import { useIntl } from 'react-intl';

import { Form, Radio, Select, Spin } from 'antd';

import Title from '@src/components/BoxContainer/components/Title';
import { UserSelector } from '@src/containers/UserSelector';
import IntlMessages from '@src/util/IntlMessages';

import { USERS_TYPE } from '../../constants';

/**
 * Combined component for selecting users type and corresponding users or groups.
 *
 * @param {Object} props
 * @param {string} props.usersType - Current usersType value (ALL, USERS, GROUPS)
 * @param {Array} props.userOptions - Options for users select
 * @param {boolean} props.usersLoading - Loading state for users
 * @param {Function} props.handlePopupScroll - Handler for users select scroll pagination
 * @param {Array} props.groupOptions - Options for groups select
 * @param {boolean} props.groupsLoading - Loading state for groups
 * @param {Function} props.handlePopupScrollGroups - Handler for groups select scroll pagination
 * @param {boolean} [props.groupsRequired] - Whether groups selection is required (default: true)
 * @param {boolean} [props.usersRequired] - Whether users selection is required (default: false)
 */
export const UsersTypeSelector = ({
  usersType,
  groupOptions,
  groupsLoading,
  handlePopupScrollGroups,
  onReset,
  divisionIds = [],
  orgId,
  userIds = [],
}) => {
  const intl = useIntl();

  const handleReset = () => onReset?.()

  return (
    <>
      <Form.Item name="usersType" className="gx-m-0">
        <Radio.Group onChange={handleReset}>
          <Radio value={USERS_TYPE.ALL}>
            <IntlMessages id="dashboards.chart.form.counter.filter.users.all" />
          </Radio>
          <Radio value={USERS_TYPE.USERS}>
            <IntlMessages id="groups.select.table.users" />
          </Radio>
          <Radio value={USERS_TYPE.GROUPS}>
            <IntlMessages id="groups.select.table.groups" />
          </Radio>
        </Radio.Group>
      </Form.Item>

      <div className="gx-mt-2">
        {usersType === USERS_TYPE.USERS && (
          <>
            <Title.LabelForm value={intl.formatMessage({ id: 'general.user' })} />
            <Form.Item
              name="userIds"
              rules={[{ required: true, message: intl.formatMessage({ id: 'form.required' }) }]}
            >
              <UserSelector
                divisionIds={divisionIds}
                userIds={userIds || []}
                orgId={orgId}
              />
            </Form.Item>
          </>
        )}

        {usersType === USERS_TYPE.GROUPS && (
          <>
            <Title.LabelForm value={intl.formatMessage({ id: 'general.group' })} />
            <Form.Item
              name="groupIds"
              rules={[{ required: true, message: intl.formatMessage({ id: 'form.required' }) }]}
            >
              <Select
                mode="multiple"
                showSearch
                allowClear
                optionFilterProp="label"
                options={groupOptions}
                loading={groupsLoading}
                notFoundContent={
                  groupsLoading ? <Spin size="small" /> : <IntlMessages id="general.empty.state" />
                }
                listHeight={200}
                onPopupScroll={handlePopupScrollGroups}
                placeholder={intl.formatMessage({ id: 'general.search' })}
                getPopupContainer={trigger => trigger.parentNode}
              />
            </Form.Item>
          </>
        )}
      </div>
    </>
  );
};

export default UsersTypeSelector;
